import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Section, Container, Eyebrow } from "@/components/primitives";
import { CTASection } from "@/components/CTASection";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/site";

export default function InsightDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/insights/${slug}`)
      .then((r) => setItem(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <Section className="py-40">
        <Container>
          <p className="font-mono text-sm text-govx-grey">Article not found.</p>
          <Link to="/insights" className="mt-4 inline-block font-semibold text-govx-green">
            ← Back to Insights
          </Link>
        </Container>
      </Section>
    );

  if (!item)
    return (
      <Section className="py-40">
        <Container>
          <p className="font-mono text-sm text-govx-grey">Loading…</p>
        </Container>
      </Section>
    );

  return (
    <>
      <Seo title={`${item.title} | GovX Intelligence`} description={item.excerpt} image={item.image_url} />
      <article>
        <section className="relative bg-govx-black pt-[74px] text-govx-paper grain">
          <Container className="relative z-10 py-16 lg:py-20">
            <Link
              to="/insights"
              data-testid="back-to-insights"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-govx-grey transition-colors hover:text-govx-lime"
            >
              <ArrowLeft className="h-4 w-4" /> GovX Intelligence
            </Link>
            <div className="mt-8 flex items-center gap-3 font-mono text-[0.62rem] uppercase tracking-[0.16em] text-govx-lime">
              <span>{item.category}</span>
              <span className="text-govx-grey">/ {item.content_type}</span>
            </div>
            <h1 className="mt-5 max-w-4xl font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
              {item.title}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-govx-grey">{item.excerpt}</p>
            <div className="mt-6 font-mono text-xs text-govx-grey">By {item.author}</div>
          </Container>
        </section>

        <Section className="py-16 lg:py-24">
          <Container>
            <div className="relative aspect-[16/9] overflow-hidden border border-govx-charcoal/10">
              <img src={item.image_url || IMAGES.lagosColor} alt={item.title} className="h-full w-full object-cover" />
            </div>
            <div className="mx-auto mt-12 max-w-2xl">
              {(item.body || item.excerpt).split("\n").filter(Boolean).map((p, i) => (
                <p key={i} className="mb-6 text-lg leading-relaxed text-govx-charcoal/80">
                  {p}
                </p>
              ))}
              <div className="mt-10 border-t border-govx-charcoal/10 pt-6">
                <Eyebrow>GovX Analysis</Eyebrow>
              </div>
            </div>
          </Container>
        </Section>
      </article>
      <CTASection eyebrow="Work With GovX" title={["Turn analysis", "into action."]} />
    </>
  );
}
